import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { User as UserIcon, Building2, Mail, MapPin } from 'lucide-react';
import apiService from '../../services/api';
import Button from '../UI/Button';
import Input from '../UI/Input';
import PhoneInput from '../UI/PhoneInput';
import Modal from '../UI/Modal';

export interface CreateCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (customer: { _id: string; name: string; company?: string; email?: string; phone?: string; address?: string; projects?: Array<{ name: string; address: string }> }) => void;
}

const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    address: '',
  });

  const handleClose = () => {
    setFormData({ name: '', company: '', phone: '', email: '', address: '' });
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: formData.name.trim(),
        company: formData.company.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined,
        address: formData.address.trim() || undefined,
        projects: [],
      };
      const res = await apiService.createCustomer(payload);
      const customer = res.data?.customer || res.data;
      toast.success('Customer created successfully');
      onSuccess(customer);
      handleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Customer" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name *"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Contact person name"
          required
          fullWidth
          icon={<UserIcon className="w-5 h-5 text-gray-400" />}
        />
        <Input
          label="Company"
          value={formData.company}
          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
          placeholder="Company name"
          fullWidth
          icon={<Building2 className="w-5 h-5 text-gray-400" />}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <PhoneInput
            id="create-customer-phone"
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="Phone number"
          />
        </div>
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="email@example.com"
          fullWidth
          icon={<Mail className="w-5 h-5 text-gray-400" />}
        />
        <Input
          label="Address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="Address"
          fullWidth
          icon={<MapPin className="w-5 h-5 text-gray-400" />}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Create Customer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateCustomerModal;
